import { Component, EventEmitter, Output } from '@angular/core';
import { BaseApplicantComponent } from '../base-applicant.component';
import { ApplicantSponsorDependant } from './../../../models/applicant-sponsor-dependant.model';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';

@Component({
  selector: 'boi-applicant-current-properties',
  templateUrl: './applicant-current-properties.component.html',
  styleUrls: ['./applicant-current-properties.component.scss']
})
export class ApplicantCurrentPropertiesComponent extends BaseApplicantComponent {
  @Output() readonly addApplicant: EventEmitter<null> = new EventEmitter();

  constructor(protected mortgageApplicationStore: MortgageApplicationStore) {
    super(mortgageApplicationStore);
  }

  isHoldingMortgageOnBtlAffordability(): boolean {
    return this.isLoanGroupTypeBtlAffordability() && this.applicant.holdsMortgage();
  }

  isRemortgageCurrentResidentialProperty(): boolean {
    return (
      this.isLoanGroupTypeBtlAffordability() &&
      this.applicant.RemortgageCurrentResidentialPropertyCode === this.referenceDataOptionLists.AnswerTypeYes
    );
  }

  canHoldAnotherMortgage(): boolean {
    return (
      (this.applicant.isOwnerOrPurchasingWithOwnOrOtherResidence() || this.isApplicantRemortgaging()) &&
      this.mortgageApplication.isResidential &&
      this.applicant.IsLoanSponsorCode !== this.referenceDataOptionLists.AnswerTypeYes
    );
  }

  isApplicantRemortgaging(): boolean {
    return !this.isLoanGroupTypeBtlAffordability() && this.applicant.isRemortgaging();
  }

  isBtlRemortgaging(): boolean {
    return this.mortgageApplication.isBuyToLet && this.applicant.isPartToRemortgage();
  }

  shouldShowMortgageOwed(): boolean {
    return this.mortgageApplication.isResidential
      ? this.applicant.isOwnerOrPurchasingWithOwnOrOtherResidence() && this.applicant.holdsMortgage()
      : this.applicant.isPartToRemortgage();
  }

  shouldDisplayAmountOwedOnRemortgagedProperty(): boolean {
    return (
      this.mortgageApplication.isResidential &&
      this.applicant.isPartToRemortgage() &&
      this.applicant.holdsMortgage() &&
      this.mortgageApplication.MortgagePayOffAmount > 0
    );
  }

  shouldDisplayRemainderFundingDetailsForApplicant(): boolean {
    return (
      this.mortgageApplication.isResidential &&
      this.applicant.AmountOwedOnRemortgagedProperty > this.mortgageApplication.MortgagePayOffAmount
    );
  }

  hasMortgagePayment(): boolean {
    return this.isResidentialAppOrFactFind() && this.applicant.ownsCurrentResidence() && this.applicant.holdsMortgage();
  }

  private isResidentialAppOrFactFind(): boolean {
    return (
      this.mortgageApplication.isResidential &&
      (this.mortgageApplication.IsFullApp || this.mortgageApplication.IsFactFind)
    );
  }

  shouldShowLenderQuestions(): boolean {
    if (this.mortgageApplication.IsAip) {
      return false;
    }

    if (this.shouldShowMortgageOwed()) {
      return this.applicant.ResidentialMortgageOwed > 0;
    }

    if (this.shouldDisplayAmountOwedOnRemortgagedProperty()) {
      return this.applicant.AmountOwedOnRemortgagedProperty > 0;
    }

    return false;
  }

  canSellProperty(): boolean {
    return this.applicant.isPurchasingOrMovingHouse();
  }

  shouldDisplayFutureCorrespondenceAddress(): boolean {
    return (
      this.mortgageApplication.isBuyToLet &&
      this.mortgageApplication.IsFullApp &&
      this.applicant.ConvertPropertyToBtlCode === this.referenceDataOptionLists.AnswerTypeYes &&
      this.applicant.isRemortgaging()
    );
  }

  canHoldAnotherNonBtl(): boolean {
    return (
      this.mortgageApplication.isResidential &&
      (this.applicant.isPurchasingOrMovingHouse() || this.applicant.isRemortgaging())
    );
  }

  holdsAnotherNonBtl(): boolean {
    return (
      this.canHoldAnotherNonBtl() &&
      this.applicant.HoldAnyOtherNonBtlCode === this.referenceDataOptionLists.AnswerTypeYes
    );
  }

  initializeSponsorDependants(): void {
    if (this.applicant.NumberOfSponsorDependants === 0) {
      this.applicant.ApplicantSponsorDependants = new Array<ApplicantSponsorDependant>();
    } else {
      if (!this.applicant.ApplicantSponsorDependants || this.applicant.ApplicantSponsorDependants.length === 0) {
        this.applicant.ApplicantSponsorDependants = new Array<ApplicantSponsorDependant>();
      }

      while (this.applicant.NumberOfSponsorDependants < this.applicant.ApplicantSponsorDependants.length) {
        this.applicant.ApplicantSponsorDependants.pop();
      }

      for (
        let i = this.applicant.ApplicantSponsorDependants.length;
        i < this.applicant.NumberOfSponsorDependants;
        i++
      ) {
        this.applicant.ApplicantSponsorDependants.push(new ApplicantSponsorDependant());
      }
    }
  }
}
